import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  approveDraft,
  fetchAccounts,
  fetchCampaigns,
  fetchDrafts,
  generateDraftScript,
  generateDraftVideo,
  rejectDraft,
} from '../lib/queries.ts';
import type { PostDraft } from '../lib/types.ts';

function formatViews(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function formatDuration(sec: number | null): string {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function useRelativeTime() {
  const { t } = useTranslation();
  return (iso: string): string => {
    const ms = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 1) return t('accounts.card.lastNow');
    if (minutes < 60) return t('accounts.card.lastMin', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('accounts.card.lastHour', { count: hours });
    return t('accounts.card.lastDay', { count: Math.floor(hours / 24) });
  };
}

const VIDEO_GRADIENTS = [
  'linear-gradient(135deg, #FFB938, #C92651)',
  'linear-gradient(135deg, #B721FF, #8B0E2A)',
  'linear-gradient(135deg, #4FAE4E, #182533)',
  'linear-gradient(135deg, #6CC0FF, #182533)',
  'linear-gradient(135deg, #25F4EE, #FE2C55)',
];

export function QueuePage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [campaignFilter, setCampaignFilter] = useState<string>('all');
  const [approvingDraft, setApprovingDraft] = useState<PostDraft | null>(null);
  const [rejectingDraft, setRejectingDraft] = useState<PostDraft | null>(null);

  const draftsQuery = useQuery({
    queryKey: ['drafts', 'list', 'pending', campaignFilter],
    queryFn: () =>
      fetchDrafts({
        moderationStatus: 'pending',
        ...(campaignFilter !== 'all' ? { campaignId: campaignFilter } : {}),
        limit: 100,
      }),
    // Если в очереди есть драфты, у которых ещё не доехали mp4-архив или
    // транскрипт — обновляемся каждые 5 секунд, чтобы было видно прогресс
    // фоновой обработки. Когда всё доехало — спокойно раз в 30 секунд.
    refetchInterval: (query) => {
      const drafts = query.state.data?.drafts ?? [];
      const anyProcessing = drafts.some(
        (d) => !d.sourceVideoR2Key || !d.sourceTranscript,
      );
      return anyProcessing ? 5_000 : 30_000;
    },
  });

  const campaignsQuery = useQuery({ queryKey: ['campaigns'], queryFn: fetchCampaigns });

  const drafts = draftsQuery.data?.drafts ?? [];
  const campaigns = campaignsQuery.data?.campaigns ?? [];

  const groupedByCampaign = useMemo(() => {
    const groups = new Map<string, PostDraft[]>();
    for (const d of drafts) {
      const list = groups.get(d.campaignId) ?? [];
      list.push(d);
      groups.set(d.campaignId, list);
    }
    return Array.from(groups.entries()).map(([campaignId, list]) => ({
      campaign: campaigns.find((c) => c.id === campaignId),
      campaignId,
      drafts: list,
    }));
  }, [drafts, campaigns]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('queue.title')}</h1>
          <div className="sub">
            {t('queue.subtitle', {
              count: drafts.length,
              label: drafts.length === 1 ? t('queue.subtitleSingular') : t('queue.subtitlePlural'),
            })}
          </div>
        </div>
        <div className="filters">
          <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)}>
            <option value="all">{t('queue.filterAll')}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="metric-row">
        <div className="metric">
          <div className="label">⏳ {t('queue.metricPending')}</div>
          <div className="value">{drafts.length}</div>
          <div className="delta">{t('queue.metricPendingHint')}</div>
        </div>
        <div className="metric amber">
          <div className="label">🎯 {t('queue.metricCampaigns')}</div>
          <div className="value">{groupedByCampaign.length}</div>
          <div className="delta">{t('queue.metricCampaignsHint')}</div>
        </div>
        <div className="metric cool">
          <div className="label">⚡ {t('queue.metricRefresh')}</div>
          <div className="value">30s</div>
          <div className="delta">{t('queue.metricRefreshHint')}</div>
        </div>
        <div className="metric">
          <div className="label">✓ {t('queue.metricHotkeys')}</div>
          <div className="value" style={{ fontSize: 16, fontFamily: 'var(--mono)' }}>
            A · R · ✕
          </div>
          <div className="delta">{t('queue.metricHotkeysHint')}</div>
        </div>
      </div>

      <div className="section">
        <div className="section-head">
          <h2>
            {t('queue.readyTitle')} <span className="count">{drafts.length}</span>
          </h2>
        </div>
        <div className="section-body no-pad">
          {draftsQuery.isLoading && <div style={{ padding: 16 }}>{t('queue.loading')}</div>}
          {!draftsQuery.isLoading && drafts.length === 0 && (
            <div style={{ padding: 24, color: 'var(--text-3)' }}>
              {t('queue.empty')}
            </div>
          )}
          {groupedByCampaign.map((group, idx) => (
            <CampaignGroup
              key={group.campaignId}
              campaignName={group.campaign?.name ?? group.campaignId.slice(0, 8)}
              drafts={group.drafts}
              gradientOffset={idx * group.drafts.length}
              onApprove={setApprovingDraft}
              onReject={setRejectingDraft}
            />
          ))}
        </div>
      </div>

      {approvingDraft && (
        <ApproveDialog
          draft={approvingDraft}
          onClose={() => setApprovingDraft(null)}
          onSuccess={() => {
            setApprovingDraft(null);
            void queryClient.invalidateQueries({ queryKey: ['drafts'] });
          }}
        />
      )}

      {rejectingDraft && (
        <RejectDialog
          draft={rejectingDraft}
          onClose={() => setRejectingDraft(null)}
          onSuccess={() => {
            setRejectingDraft(null);
            void queryClient.invalidateQueries({ queryKey: ['drafts'] });
          }}
        />
      )}
    </>
  );
}

interface CampaignGroupProps {
  campaignName: string;
  drafts: PostDraft[];
  gradientOffset: number;
  onApprove: (draft: PostDraft) => void;
  onReject: (draft: PostDraft) => void;
}

function CampaignGroup(props: CampaignGroupProps): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div>
      <div
        style={{
          padding: '10px 16px',
          background: 'var(--bg-input)',
          borderBottom: '1px solid var(--line)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: 'var(--neon)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        ▾ {t('queue.groupHeader', { name: props.campaignName, count: props.drafts.length })}
      </div>
      {props.drafts.map((draft, idx) => (
        <DraftCard
          key={draft.id}
          draft={draft}
          gradient={VIDEO_GRADIENTS[(props.gradientOffset + idx) % VIDEO_GRADIENTS.length]!}
          onApprove={() => props.onApprove(draft)}
          onReject={() => props.onReject(draft)}
        />
      ))}
    </div>
  );
}

interface DraftCardProps {
  draft: PostDraft;
  gradient: string;
  onApprove: () => void;
  onReject: () => void;
}

function DraftCard({ draft, gradient, onApprove, onReject }: DraftCardProps): React.ReactElement {
  const { t } = useTranslation();
  const relativeTime = useRelativeTime();
  const queryClient = useQueryClient();
  const [scriptError, setScriptError] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const [hintDraft, setHintDraft] = useState<string>(draft.moderatorScriptHint ?? '');

  // Каждые 5 секунд polling приносит новый presigned URL для одного и того же
  // mp4 в R2 (срок жизни — 1 час, signature разная). Если присваивать <video src>
  // меняющийся URL — браузер перезагружает видео и play обнуляется. Замораживаем
  // URL на первом ненулевом значении: пока компонент жив, src не меняется.
  const stableSourceUrlRef = useRef<string | null>(null);
  if (draft.sourceVideoUrl && !stableSourceUrlRef.current) {
    stableSourceUrlRef.current = draft.sourceVideoUrl;
  }
  const sourceVideoUrl = stableSourceUrlRef.current;

  const scriptMutation = useMutation({
    mutationFn: () =>
      generateDraftScript(draft.id, {
        moderatorHint: hintDraft.trim() ? hintDraft.trim() : null,
      }),
    onMutate: () => setScriptError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
    onError: (e: Error) => setScriptError(e.message),
  });

  const videoMutation = useMutation({
    mutationFn: () => generateDraftVideo(draft.id),
    onMutate: () => {
      setVideoError(null);
      setGeneratedVideoUrl(null);
    },
    onSuccess: (data) => {
      setGeneratedVideoUrl(data.videoUrl);
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
    onError: (e: Error) => setVideoError(e.message),
  });

  // Premium tier: Google Veo 3 Fast через Replicate. 8 сек, 1080p,
  // встроенное аудио (речь / эффекты / атмосфера). Качество близко
  // к Sora-уровню, цена ~$3 за клип.
  const premiumVideoMutation = useMutation({
    mutationFn: () =>
      generateDraftVideo(draft.id, {
        model: 'google/veo-3-fast',
        durationSec: 8,
        aspectRatio: '9:16',
      }),
    onMutate: () => {
      setVideoError(null);
      setGeneratedVideoUrl(null);
    },
    onSuccess: (data) => {
      setGeneratedVideoUrl(data.videoUrl);
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
    },
    onError: (e: Error) => setVideoError(e.message),
  });

  const anyVideoPending = videoMutation.isPending || premiumVideoMutation.isPending;

  return (
    <div className="gen-post">
      <div
        className={`video-thumb${sourceVideoUrl ? ' has-video' : ''}`}
        style={{ background: gradient, position: 'relative', overflow: 'hidden' }}
      >
        {sourceVideoUrl ? (
          <video
            src={sourceVideoUrl}
            controls
            preload="metadata"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              background: '#000',
            }}
          />
        ) : null}
        <div
          className="duration"
          style={{
            position: 'absolute',
            bottom: 6,
            right: 6,
            zIndex: 2,
            pointerEvents: 'none',
          }}
        >
          {formatDuration(draft.videoDurationSec)}
        </div>
      </div>
      <div className="body">
        <div className="top">
          <span className="source">
            {t('queue.source', {
              platform: draft.sourcePlatform,
              author: draft.sourceAuthorHandle ?? '',
              views: formatViews(draft.sourceViewsCount),
            })}
          </span>
          <span style={{ color: 'var(--text-3)', fontFamily: 'var(--mono)', fontSize: 11 }}>
            {relativeTime(draft.createdAt)}
          </span>
        </div>
        <div className="caption">{draft.caption}</div>
        <div className="hashtags">
          {draft.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}
        </div>

        {draft.sourceTranscript && (
          <details
            style={{
              marginTop: 8,
              background: 'rgba(108,192,255,0.08)',
              border: '1px solid rgba(108,192,255,0.25)',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 12,
            }}
          >
            <summary
              style={{
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: '#6CC0FF',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
              }}
            >
              🎙 {t('queue.transcript_label')} · {draft.sourceTranscriptModel ?? 'whisper'}
            </summary>
            <div
              style={{
                marginTop: 6,
                color: '#cfe8ff',
                lineHeight: 1.45,
                fontSize: 12,
                fontStyle: 'italic',
                wordBreak: 'break-word',
              }}
            >
              {draft.sourceTranscript}
            </div>
          </details>
        )}

        <div style={{ marginTop: 10 }}>
          <label
            style={{
              display: 'block',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 4,
            }}
          >
            {t('queue.script_hint_label')}
          </label>
          <textarea
            value={hintDraft}
            onChange={(e) => setHintDraft(e.target.value)}
            placeholder={t('queue.script_hint_placeholder')}
            rows={2}
            maxLength={800}
            style={{
              width: '100%',
              fontSize: 12,
              fontFamily: 'inherit',
              padding: '6px 8px',
              borderRadius: 4,
              border: '1px solid var(--line)',
              background: 'var(--bg-input)',
              color: 'var(--text)',
              resize: 'vertical',
              minHeight: 36,
            }}
          />
        </div>

        {draft.script && <ScriptPanel script={draft.script} model={draft.scriptModel} />}
        {scriptError && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--hot)',
              marginTop: 6,
              fontFamily: 'var(--mono)',
            }}
          >
            {scriptError}
          </div>
        )}
        {(draft.generatedVideoR2Key || anyVideoPending || videoError) && (
          <VideoPanel
            isPending={anyVideoPending}
            error={videoError}
            videoUrl={generatedVideoUrl}
            model={draft.generatedVideoModel}
            generatedAt={draft.generatedVideoAt}
            hasStoredVideo={Boolean(draft.generatedVideoR2Key)}
          />
        )}
      </div>
      <div className="actions">
        <div className="scheduled">{t('queue.scheduled')}</div>
        <div className="btn-row">
          <button className="btn-mini approve" onClick={onApprove} title="A">
            ✓
          </button>
          <button
            className="btn-mini regen"
            onClick={() => scriptMutation.mutate()}
            disabled={scriptMutation.isPending}
            title={
              draft.script
                ? t('queue.script_regen_tooltip')
                : t('queue.script_generate_tooltip')
            }
            style={{
              opacity: scriptMutation.isPending ? 0.5 : 1,
              cursor: scriptMutation.isPending ? 'wait' : 'pointer',
            }}
          >
            {scriptMutation.isPending ? '…' : draft.script ? '↻' : '📝'}
          </button>
          <button
            className="btn-mini regen"
            onClick={() => videoMutation.mutate()}
            disabled={!draft.script || anyVideoPending}
            title={
              !draft.script
                ? t('queue.video_disabled_tooltip')
                : draft.generatedVideoR2Key
                  ? t('queue.video_regen_tooltip')
                  : t('queue.video_generate_tooltip')
            }
            style={{
              opacity: !draft.script ? 0.4 : anyVideoPending ? 0.5 : 1,
              cursor: !draft.script
                ? 'not-allowed'
                : anyVideoPending
                  ? 'wait'
                  : 'pointer',
            }}
          >
            {videoMutation.isPending ? '…' : '🎬'}
          </button>
          <button
            className="btn-mini regen"
            onClick={() => premiumVideoMutation.mutate()}
            disabled={!draft.script || anyVideoPending}
            title={
              !draft.script
                ? t('queue.video_disabled_tooltip')
                : t('queue.video_premium_tooltip')
            }
            style={{
              opacity: !draft.script ? 0.4 : anyVideoPending ? 0.5 : 1,
              cursor: !draft.script
                ? 'not-allowed'
                : anyVideoPending
                  ? 'wait'
                  : 'pointer',
              background: !draft.script ? undefined : 'rgba(255,185,56,0.12)',
              color: !draft.script ? undefined : '#FFB938',
              borderColor: !draft.script ? undefined : 'rgba(255,185,56,0.4)',
            }}
          >
            {premiumVideoMutation.isPending ? '…' : '🌟'}
          </button>
          <button className="btn-mini reject" onClick={onReject} title="✕">
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

function VideoPanel({
  isPending,
  error,
  videoUrl,
  model,
  generatedAt,
  hasStoredVideo,
}: {
  isPending: boolean;
  error: string | null;
  videoUrl: string | null;
  model: string | null;
  generatedAt: string | null;
  hasStoredVideo: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const borderColor = error
    ? 'rgba(255,61,110,0.5)'
    : isPending
      ? 'rgba(108,192,255,0.5)'
      : 'rgba(64,224,160,0.5)';
  const tint = error
    ? 'rgba(255,61,110,0.10)'
    : isPending
      ? 'rgba(108,192,255,0.10)'
      : 'rgba(64,224,160,0.10)';

  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 12px',
        background: tint,
        border: `1px solid ${borderColor}`,
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: error ? '#FF3D6E' : isPending ? '#6CC0FF' : '#40E0A0',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        🎬 {t('queue.video_label')}
        {model && ` · ${model.split('/').pop()}`}
        {generatedAt && ` · ${new Date(generatedAt).toLocaleTimeString()}`}
      </div>
      {isPending && (
        <div style={{ marginTop: 6, color: '#cfe8ff', fontSize: 12 }}>
          {t('queue.video_running')}
        </div>
      )}
      {error && (
        <div style={{ marginTop: 6, color: '#FF3D6E', fontSize: 11, fontFamily: 'var(--mono)' }}>
          {error}
        </div>
      )}
      {videoUrl && (
        <video
          src={videoUrl}
          controls
          style={{
            marginTop: 8,
            maxWidth: 220,
            borderRadius: 4,
            display: 'block',
          }}
        />
      )}
      {!videoUrl && hasStoredVideo && !isPending && !error && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#9aa0b0' }}>
          {t('queue.video_stored_no_preview')}
        </div>
      )}
    </div>
  );
}

function ScriptPanel({
  script,
  model,
}: {
  script: NonNullable<PostDraft['script']>;
  model: string | null;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <details
      open
      style={{
        marginTop: 10,
        background: 'rgba(255,185,56,0.10)',
        border: '1px solid rgba(255,185,56,0.45)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 13,
        color: 'var(--text-1, #ffffff)',
      }}
    >
      <summary
        style={{
          cursor: 'pointer',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: '#FFB938',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        📝 {t('queue.script_label')} · {script.durationSec}s · {script.language}
        {model && ` · ${model}`}
      </summary>
      <div style={{ marginTop: 10, lineHeight: 1.55 }}>
        <ScriptRow label={t('queue.script_hook')} value={script.hook} />
        <ScriptRow label={t('queue.script_body')} value={script.body} />
        <ScriptRow label={t('queue.script_cta')} value={script.cta} />
        {script.voiceLine && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 10px',
              background: 'rgba(192,132,252,0.10)',
              border: '1px solid rgba(192,132,252,0.35)',
              borderRadius: 4,
              fontSize: 13,
              color: '#e9d5ff',
              lineHeight: 1.55,
              fontStyle: 'italic',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10,
                color: '#c084fc',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 700,
                marginRight: 8,
              }}
            >
              🔊 {t('queue.script_voice')}
            </span>
            «{script.voiceLine}»
          </div>
        )}
        {script.hashtags.length > 0 && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 8px',
              background: 'rgba(255,255,255,0.04)',
              borderRadius: 4,
              fontSize: 12,
              color: '#9aa0b0',
            }}
          >
            {script.hashtags.map((h) => `#${h}`).join(' ')}
          </div>
        )}
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: 'rgba(108,192,255,0.10)',
            border: '1px solid rgba(108,192,255,0.35)',
            borderRadius: 4,
            fontFamily: 'var(--mono)',
            fontSize: 11,
            color: '#cfe8ff',
            wordBreak: 'break-word',
            lineHeight: 1.55,
          }}
        >
          <span style={{ color: '#6CC0FF', fontWeight: 600, marginRight: 6 }}>visual:</span>
          {script.visualPrompt}
        </div>
      </div>
    </details>
  );
}

function ScriptRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div style={{ marginTop: 4 }}>
      <span
        style={{
          color: '#FFB938',
          fontWeight: 600,
          marginRight: 6,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}:
      </span>
      <span style={{ color: '#f0f1f5' }}>{value}</span>
    </div>
  );
}

interface ApproveDialogProps {
  draft: PostDraft;
  onClose: () => void;
  onSuccess: () => void;
}

function ApproveDialog({ draft, onClose, onSuccess }: ApproveDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const accountsQuery = useQuery({
    queryKey: ['accounts', { status: 'active' }],
    queryFn: () => fetchAccounts({ status: 'active' }),
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [scheduledFor, setScheduledFor] = useState<string>(() => {
    const next = new Date(Date.now() + 5 * 60_000);
    return next.toISOString().slice(0, 16);
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      approveDraft(draft.id, {
        accountIds: Array.from(selectedIds),
        scheduledFor: new Date(scheduledFor).toISOString(),
      }),
    onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  function toggle(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const accounts = accountsQuery.data?.accounts ?? [];

  return (
    <ModalShell onClose={onClose} width={520} title={t('queue.approve_dialog.title')}>
      <div style={{ color: 'var(--text-3)', fontSize: 12, marginBottom: 16 }}>
        {t('queue.approve_dialog.subtitle')}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div className="form-row">
          <label>{t('queue.approve_dialog.accounts', { count: selectedIds.size })}</label>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 220,
              overflowY: 'auto',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: 8,
            }}
          >
            {accountsQuery.isLoading && <div>{t('queue.approve_dialog.accountsLoading')}</div>}
            {accounts.length === 0 && !accountsQuery.isLoading && (
              <div style={{ color: 'var(--text-3)', fontSize: 12 }}>
                {t('queue.approve_dialog.accountsEmpty')}
              </div>
            )}
            {accounts.map((acc) => (
              <label
                key={acc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  padding: 6,
                  borderRadius: 6,
                  background: selectedIds.has(acc.id) ? 'var(--neon-soft)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(acc.id)}
                  onChange={() => toggle(acc.id)}
                />
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>
                  {t(`accounts.platformShort.${acc.platform}`)}
                </span>
                <span>{acc.handle}</span>
                {acc.displayName && (
                  <span style={{ color: 'var(--text-3)', fontSize: 12 }}>· {acc.displayName}</span>
                )}
              </label>
            ))}
          </div>
        </div>

        <div className="form-row">
          <label>{t('queue.approve_dialog.scheduledFor')}</label>
          <input
            type="datetime-local"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 12px',
            background: 'rgba(255,61,110,0.1)',
            border: '1px solid rgba(255,61,110,0.3)',
            borderRadius: 6,
            color: 'var(--hot)',
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn-mini" style={{ padding: '8px 14px' }}>
          {t('common.cancel')}
        </button>
        <button
          className="btn-mini approve"
          onClick={() => mutation.mutate()}
          disabled={selectedIds.size === 0 || mutation.isPending}
          style={{ padding: '8px 14px', fontSize: 13 }}
        >
          {mutation.isPending
            ? t('queue.approve_dialog.submitting')
            : t('queue.approve_dialog.submit', { count: selectedIds.size })}
        </button>
      </div>
    </ModalShell>
  );
}

interface RejectDialogProps {
  draft: PostDraft;
  onClose: () => void;
  onSuccess: () => void;
}

function RejectDialog({ draft, onClose, onSuccess }: RejectDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => rejectDraft(draft.id, notes || null),
    onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <ModalShell onClose={onClose} width={420} title={t('queue.reject_dialog.title')}>
      <div className="form-row">
        <label>{t('queue.reject_dialog.notes')}</label>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('queue.reject_dialog.notesPlaceholder')}
        />
      </div>
      {error && (
        <div style={{ color: 'var(--hot)', fontSize: 12, marginBottom: 8 }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn-mini" style={{ padding: '8px 14px' }}>
          {t('common.cancel')}
        </button>
        <button
          className="btn-mini reject"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          style={{ padding: '8px 14px', fontSize: 13 }}
        >
          {mutation.isPending
            ? t('queue.reject_dialog.submitting')
            : t('queue.reject_dialog.submit')}
        </button>
      </div>
    </ModalShell>
  );
}

interface ModalShellProps {
  onClose: () => void;
  title: string;
  width: number;
  children: React.ReactNode;
}

function ModalShell({ onClose, title, width, children }: ModalShellProps): React.ReactElement {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width,
          maxHeight: '85vh',
          overflowY: 'auto',
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
